import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { agenciesApi, workspacesApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';

/**
 * Keeps the active agency/workspace selection valid. Mounted once in Shell so it
 * runs regardless of whether the sidebar switcher is mounted (it isn't while the
 * sidebar is collapsed). Shares query keys with the switcher, so no extra fetches.
 */
export function useScopeAutoSelect() {
  const { getToken } = useAuth();
  const { currentAgency, currentWorkspace, setCurrentAgency, setCurrentWorkspace } = useAppStore();

  const { data: agenciesData } = useQuery({
    queryKey: ['agencies'],
    queryFn: async () => {
      const token = await getToken();
      return agenciesApi.list(token!);
    },
  });

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  // Auto-select the first agency once loaded if none is active. (react-query v5
  // dropped useQuery's onSuccess, so this must run as an effect on the data.)
  useEffect(() => {
    if (!currentAgency && agenciesData?.agencies.length) {
      setCurrentAgency(agenciesData.agencies[0]);
    }
  }, [agenciesData, currentAgency, setCurrentAgency]);

  // Auto-select a workspace when none is active, or when the persisted one no
  // longer belongs to the active agency (e.g. right after switching agencies).
  // An empty list (e.g. a scoped member whose only brand was deleted) clears the
  // stale selection so pages show their empty state instead of 403/404s.
  useEffect(() => {
    const list = workspacesData?.workspaces;
    if (!list) return;
    if (list.length === 0) {
      if (currentWorkspace) setCurrentWorkspace(null);
      return;
    }
    if (!currentWorkspace || !list.some((w) => w.id === currentWorkspace.id)) {
      setCurrentWorkspace(list[0]);
    }
  }, [workspacesData, currentWorkspace, setCurrentWorkspace]);
}
